import { orderAddressCountry } from "./address-country";
/**
 * The pure half of the Packing surface: the constants and types its wire contract is made of,
 * and the folds that turn rows into what the page shows — the stock allocation, the pick sheet,
 * the sub-status and the address readers.
 *
 * Separate from `order/packing.ts` because the dashboard SPA imports from here. That module
 * imports `shared/db.ts`, which constructs an `AsyncLocalStorage` from `node:async_hooks` at
 * module load, so a browser bundle that reaches it throws
 * `AsyncLocalStorage is not a constructor` before anything renders. Nothing in this file may
 * import a database, a driver or a schema table; `packing.test.ts` covers it, and
 * `packing-fold.browser.test.ts` fails if that ever changes.
 */

import type {
  FulfillmentMethod,
  OrderLine,
  OrderLineStatus,
  OrderStatus,
  OrderType,
} from "./types";

/**
 * Bag types that reach a warehouse shelf. `TCG` is a boxed-origin bag, `WITHDRAW_ASSET` one
 * Loot OS created itself; they are the same physical thing with a different label by origin.
 * `MARKETPLACE` is deliberately absent: it is bought on a vendor's site (`MANUAL_BUY`), never
 * picked from our stock, and Retool's app filtered `type: TCG` for the same reason.
 */
export const PACKABLE_ORDER_TYPES = ["TCG", "WITHDRAW_ASSET"] as const satisfies OrderType[];

/**
 * The one method a `WITHDRAW_ASSET` bag is picked by. A withdrawal of a marketplace unit is the
 * same type but `MANUAL_BUY` / `BUY_BOT`: bought on the vendor's site and never on our shelves,
 * the same reason `MARKETPLACE` is not a packable type.
 */
export const PACKABLE_WITHDRAWAL_METHOD = "FACILITY_SHIP" satisfies FulfillmentMethod;

/** Statuses the queue shows unless the caller narrows it. `PROCESSING` is the working set. */
export const PACKING_QUEUE_STATUSES = ["PROCESSING"] as const satisfies OrderStatus[];

/**
 * Retool's first two tabs, as a server-side filter. A bag is unpacked until a line is packed and
 * partial once some are — the third tab, Missing Cards, is a different report, not a bag filter.
 */
/** Retool's Category select offers this alongside the games: a bag holding more than one. */
export const MIXED_CATEGORY = "MIXED";

export const PACK_STATES = ["unpacked", "partial"] as const;
export type PackState = (typeof PACK_STATES)[number];

export const PACKING_QUEUE_SORTS = ["createdAt", "lineCount", "value", "tenant"] as const;
export type PackingQueueSort = (typeof PACKING_QUEUE_SORTS)[number];

/** Matches {@link ALLOCATION_SCAN_LIMIT}: the grid holds every bag the allocation judged, so the
 * positional window can reach all of them. A smaller page left later offsets selecting nothing. */
export const PACKING_QUEUE_MAX_LIMIT = 500;
/**
 * How far down the queue the stock allocation looks. It has to see every bag ahead of the page,
 * because an earlier bag's claim is what makes a later one short — so this is a scan bound, not a
 * page size. Beyond it the allocation is approximate, as Retool's is beyond its own fetch budget.
 *
 * This is the thing to revisit first if `PROCESSING` ever runs to thousands. The exact fix is to
 * scan only the items the page actually contends for rather than every bag ahead of it;
 * `PACKING_PR_NOTES.md` sets out that option and three others.
 */
export const ALLOCATION_SCAN_LIMIT = 500;
export const PACKING_QUEUE_DEFAULT_LIMIT = 20;
/** One sheet's worth of orders. Above this a packer is not picking, they are re-planning. */
export const PICK_SHEET_MAX_ORDERS = 200;

export interface PackingQueueFilter {
  /** Matches an order reference, or the shopper's username / external id. */
  query?: string;
  tenantId?: string;
  statuses?: OrderStatus[];
  types?: OrderType[];
  /** One of `attributes.details.category`: POKEMON, MTG, … `catalog_items.category` is always TCG. */
  category?: string;
  createdFrom?: Date;
  createdTo?: Date;
  /** Which tab: nothing packed yet, or some packed and some not. */
  packState?: PackState;
  /** Retool always hides them; this exists so a caller can ask for them deliberately. */
  includeLockedAccounts?: boolean;
  /**
   * Weeks after which an unfillable bag reserves its stock anyway, so an old bag is not starved
   * by newer ones. Retool's `SLA` input, and **0 — the default there and here — means every bag
   * reserves**, because every bag is at least 0 weeks old. Raising it narrows that to bags past
   * the threshold.
   */
  slaWeeks?: number;
  /**
   * Retool's "Include Out Of Stock" toggle, off by default: a bag whose shelf cannot cover it
   * is hidden, because a packer sent to find it comes back empty-handed.
   */
  includeOutOfStock?: boolean;
}

export interface PackingQueueInput {
  filter?: PackingQueueFilter;
  sort?: PackingQueueSort;
  direction?: "asc" | "desc";
  /** Where the packer's batch starts: "twenty orders, beginning at fifty". */
  offset?: number;
  limit?: number;
}

export interface PackingQueueRow {
  orderId: string;
  /** Boxed's `pk`. The deep link back to the bag in Retool, where PACKED is written. */
  tenantReferenceId: string | null;
  tenantId: string;
  endUserId: string;
  username: string | null;
  externalId: string;
  status: OrderStatus;
  type: OrderType;
  method: FulfillmentMethod | null;
  createdAt: Date;
  updatedAt: Date;
  /** The shopper's real name, off the shipping address. Null when the address carries none. */
  shopperName: string | null;
  lineCount: number;
  packedLineCount: number;
  subStatus: PackingSubStatus;
  totalValueCents: number;
  isSlowShip: boolean;
  /** The ISO code the bag ships to (`orderAddressCountry`), whatever shape the address is in. */
  shipsToCountry: string | null;
  /** Game categories across the bag's lines: POKEMON, MTG, LORCANA … */
  categories: string[];
  /** Every active line has stock on the shelf, which is what Retool's "Is in stock" tick means. */
  inStock: boolean;
}

export interface PackingQueueResult {
  /**
   * The scan hit {@link ALLOCATION_SCAN_LIMIT}, so `total` counts only the bags scanned and the
   * stock answer past that point is approximate. The page says so rather than quietly
   * under-reporting a long queue.
   */
  scanTruncated: boolean;
  rows: PackingQueueRow[];
  /** Rows matching the filter, so the page can say "51–70 of 412". */
  total: number;
  offset: number;
  limit: number;
}

/**
 * The shopper's own name, which only the shipping address carries — `end_users` stores a
 * username and an external id and no real name. Read by any of the address's shapes
 * (`orderAddressCountry` lists them).
 */
export function readAddressName(value: unknown): string | null {
  if (value === null || typeof value !== "object") return null;
  const address = value as Record<string, unknown>;
  const text = (key: string) => (typeof address[key] === "string" ? (address[key] as string) : "");
  const joined = [text("firstName"), text("lastName")].filter(Boolean).join(" ").trim();
  return joined || text("name").trim() || null;
}

/** Where an order stands with the packer, the way Retool's SubStatus badge reads it. */
/** Retool's three SubStatus values. A fully packed bag is waiting on shipping, not on a picker. */
export type PackingSubStatus = "NOT_PACKED" | "PARTIALLY_PACKED" | "READY_TO_SHIP";

export function resolveSubStatus(lineCount: number, packedCount: number): PackingSubStatus {
  if (packedCount === 0) return "NOT_PACKED";
  return packedCount >= lineCount ? "READY_TO_SHIP" : "PARTIALLY_PACKED";
}

/** A line as the allocation sees it: which bag, which catalog item, and whether it is still to pick. */
export interface AllocationLine {
  orderId: string;
  lineId: string;
  itemId: string;
  status: OrderLineStatus;
}

export interface AllocationOrder {
  orderId: string;
  createdAt: Date;
}

export interface AllocationResult {
  /** Bags whose every pending line the shelf could still cover, in queue order. */
  fillable: Set<string>;
  /** Per line: stock left after this bag's claim. Negative means the shelf ran out. */
  remainingByLine: Map<string, number>;
}

/** A week, which is the unit Retool's SLA input is really in. */
const WEEK_MS = 1000 * 60 * 60 * 24 * 7;

/**
 * Whether each bag can be filled, decided the way the warehouse decides it: **as a competition**.
 *
 * Retool walks the queue oldest first over one running stock pool, so an earlier bag takes the
 * cards and a later bag wanting the same card reads out of stock even though the shelf is not
 * empty. A per-bag `quantity > 0` check — which is what we had — disagrees with the packers
 * whenever two bags want the same card, which for a card shop is most of the time.
 *
 * A bag at or past `slaWeeks` reserves its stock even when it cannot be filled, so an old bag is
 * not starved forever by newer ones. **0, the default, means every bag reserves**: every bag is at
 * least 0 weeks old, which is what Retool's `SLA` input does at its own default of 0. An earlier
 * version guarded this with `slaWeeks > 0` and so reserved nothing by default — the opposite
 * behaviour, and the unsafe direction, because the stock an unfillable bag released was then
 * promised to a newer bag the warehouse could not fill either.
 *
 * Pure: the caller loads the orders, their lines and the on-hand counts.
 */
export function allocateStock(args: {
  /** Oldest first — the order the warehouse works in, and what makes the result stable. */
  orders: AllocationOrder[];
  lines: AllocationLine[];
  /** `catalog_items.quantity` per item id. A missing entry is treated as no stock. */
  onHand: Map<string, number>;
  slaWeeks?: number;
  now?: Date;
}): AllocationResult {
  const slaWeeks = args.slaWeeks ?? 0;
  const now = args.now ?? new Date();
  const linesByOrder = new Map<string, AllocationLine[]>();
  for (const line of args.lines) {
    const existing = linesByOrder.get(line.orderId);
    if (existing) existing.push(line);
    else linesByOrder.set(line.orderId, [line]);
  }

  const pool = new Map(args.onHand);
  const fillable = new Set<string>();
  const remainingByLine = new Map<string, number>();

  for (const order of args.orders) {
    let canFill = true;
    // What this bag alone has claimed so far, so a second copy of a card needs a second unit.
    const claimed = new Map<string, number>();

    for (const line of linesByOrder.get(order.orderId) ?? []) {
      if (line.status !== "PENDING") continue;
      const needed = (claimed.get(line.itemId) ?? 0) + 1;
      const available = pool.get(line.itemId) ?? 0;
      remainingByLine.set(line.lineId, available - needed);
      if (available < needed) canFill = false;
      claimed.set(line.itemId, needed);
    }

    if (canFill) fillable.add(order.orderId);

    // A bag at or past the SLA holds its stock either way, so newer bags cannot keep jumping the
    // queue. At the default of 0 every bag is past it, so every bag reserves — see the note on
    // `slaWeeks`. Reserving errs toward reporting a bag short, which is the safe direction.
    const ageWeeks = (now.getTime() - order.createdAt.getTime()) / WEEK_MS;
    const forceReserve = ageWeeks >= slaWeeks;
    if (canFill || forceReserve) {
      for (const [itemId, count] of claimed) {
        pool.set(itemId, (pool.get(itemId) ?? 0) - count);
      }
    }
  }

  return { fillable, remainingByLine };
}

/** A day, the unit the missing-card SLA and the procurement window are counted in. */
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Bags newer than this stay out of the missing-card walk unless the page asks for all of them.
 * Retool's "Show All Missing Cards" is off by default, and the cutoff is applied before the walk,
 * so a newer bag does not take stock from an older one it is hiding.
 */
export const MISSING_CARD_MIN_AGE_DAYS = 14;

/** A bag ships in 28 days, or 35 when any of its lines is slow-ship. */
export const MISSING_CARD_SLA_DAYS = 28;
export const MISSING_CARD_SLOW_SLA_DAYS = 35;

/**
 * How far back "amount procured" looks. Retool labels the column "last 4 weeks" and computes
 * `3 * 7` days; the label is what the page shows, and this is the window the number uses.
 */
export const MISSING_CARD_PROCUREMENT_WINDOW_DAYS = 21;

/** The printed urgent list keeps cards this close to (or past) their shipping deadline. */
export const URGENT_MISSING_SLA_DAYS = 7;

/** Retool takes the oldest 48 urgent cards, then prints them twelve to a page. */
export const URGENT_MISSING_CARDS_LIMIT = 48;
export const MISSING_CARDS_PER_PAGE = 12;

/** `createdAt <=` this is the default missing-card cutoff. */
export function missingCardAgeCutoff(now: Date): Date {
  return new Date(now.getTime() - MISSING_CARD_MIN_AGE_DAYS * MS_PER_DAY);
}

/** The start of the procurement window, inclusive. */
export function missingCardProcurementWindowStart(now: Date): Date {
  return new Date(now.getTime() - MISSING_CARD_PROCUREMENT_WINDOW_DAYS * MS_PER_DAY);
}

/**
 * Days until the bag's shipping deadline. Negative means it is already past, and `Math.ceil`
 * matches Retool, so a deadline a fraction of a day ago still reads 0.
 */
export function daysUntilOverSla(createdAt: Date, slaDays: number, now: Date): number {
  const deadline = createdAt.getTime() + slaDays * MS_PER_DAY;
  return Math.ceil((deadline - now.getTime()) / MS_PER_DAY);
}

/** One line as the missing-card walk sees it. `onHand` is the catalog quantity boxed copies onto every line. */
export interface MissingCardLineInput {
  lineId: string;
  itemId: string;
  status: OrderLineStatus;
  isSlowShip: boolean;
  onHand: number;
  name: string;
  imageUrl: string | null;
  priceCents: number;
  printing: string | null;
  category: string | null;
  sourceUrl: string | null;
}

/** A bag already past the age and lock filters. The date range is applied later, inside the fold. */
export interface MissingCardOrderInput {
  orderId: string;
  createdAt: Date;
  customerName: string | null;
  accountId: string | null;
  lines: MissingCardLineInput[];
}

/**
 * Procurement for one card. The loader aggregates units; the fold only copies these onto the rows.
 * `amountInWindow` counts units whose ordered time falls in {@link missingCardProcurementWindowStart}.
 */
export interface MissingCardProcurement {
  itemId: string;
  lastOrderedAt: Date | null;
  amountInWindow: number;
  orderedAtInWindow: Date[];
}

/** One short pending line, which is one row of Retool's missing-cards table. */
export interface MissingCardLine {
  lineId: string;
  orderId: string;
  accountId: string | null;
  customerName: string | null;
  createdAt: Date;
  itemId: string;
  name: string;
  imageUrl: string | null;
  priceCents: number;
  printing: string | null;
  category: string | null;
  sourceUrl: string | null;
  /** Catalog quantity, Retool's "In Stock" column. */
  onHand: number;
  /** Stock left after this line's claim. Negative, which is why the row is here. */
  quantRemaining: number;
  /** Sum of every line's price on the bag, packed lines included. */
  orderCostCents: number;
  slaDays: number;
  daysUntilOverSla: number;
  daysOverSla: number;
  isOverSla: boolean;
  lastOrderedAt: Date | null;
  /** Units ordered inside the 21-day window. The column is still labelled "last 4 weeks". */
  amountProcuredLast4Weeks: number;
  procurementDatesLast4Weeks: Date[];
}

/** One order stub on a grouped card. The bounty list prints these only as a count. */
export interface GroupedMissingCardOrder {
  orderId: string;
  accountId: string | null;
  customerName: string | null;
  createdAt: Date;
}

/**
 * The same short lines, one row per card. `quantRemaining` is the most negative line, which is
 * how many the walk still needed after the last copy. Sorted by the earliest waiting bag.
 */
export interface GroupedMissingCard {
  itemId: string;
  name: string;
  imageUrl: string | null;
  priceCents: number;
  printing: string | null;
  sourceUrl: string | null;
  quantRemaining: number;
  lastOrderedAt: Date | null;
  amountProcuredLast4Weeks: number;
  procurementDatesLast4Weeks: Date[];
  earliestOrderAt: Date;
  orders: GroupedMissingCardOrder[];
}

export interface MissingCardsFold {
  lines: MissingCardLine[];
  grouped: GroupedMissingCard[];
}

const NO_PROCUREMENT: Omit<MissingCardProcurement, "itemId"> = {
  lastOrderedAt: null,
  amountInWindow: 0,
  orderedAtInWindow: [],
};

/**
 * Retool's `missing_cards` function.
 *
 * Walks bags oldest first. Each pending line needs one unit, and `quantRemaining` is the stock
 * left after that claim, counting earlier pending lines of the same card on the same bag. The
 * on-hand figure is the first line's quantity for that card. Every bag then reserves what it
 * claimed: Retool's age check is `weeks >= 0`, which is always true, so a bag that cannot be
 * filled still holds its stock.
 *
 * A line is reported only when it is still `PENDING` and `quantRemaining` is negative. The start
 * and end dates narrow that list afterwards, so a bag outside the range still took its stock.
 */
export function foldMissingCards(input: {
  orders: MissingCardOrderInput[];
  procurements?: MissingCardProcurement[];
  now?: Date;
  createdFrom?: Date;
  createdTo?: Date;
}): MissingCardsFold {
  const now = input.now ?? new Date();
  const walked = [...input.orders].sort((a, b) => {
    const byDate = a.createdAt.getTime() - b.createdAt.getTime();
    if (byDate !== 0) return byDate;
    return a.orderId < b.orderId ? -1 : a.orderId > b.orderId ? 1 : 0;
  });

  const pool = new Map<string, number>();
  for (const order of walked) {
    for (const line of order.lines) {
      if (!pool.has(line.itemId)) pool.set(line.itemId, line.onHand);
    }
  }

  const remainingByLine = new Map<string, number>();
  for (const order of walked) {
    const claimed = new Map<string, number>();
    for (const line of order.lines) {
      if (line.status !== "PENDING") continue;
      const needed = 1 + (claimed.get(line.itemId) ?? 0);
      const available = pool.get(line.itemId) ?? 0;
      remainingByLine.set(line.lineId, available - needed);
      claimed.set(line.itemId, needed);
    }
    for (const [itemId, count] of claimed) {
      pool.set(itemId, (pool.get(itemId) ?? 0) - count);
    }
  }

  const ranged = walked.filter((order) =>
    inCreatedRange(order.createdAt, input.createdFrom, input.createdTo),
  );
  const procurementByItem = new Map((input.procurements ?? []).map((row) => [row.itemId, row]));
  const lines: MissingCardLine[] = [];

  for (const order of ranged) {
    const slaDays = order.lines.some((line) => line.isSlowShip)
      ? MISSING_CARD_SLOW_SLA_DAYS
      : MISSING_CARD_SLA_DAYS;
    const daysUntil = daysUntilOverSla(order.createdAt, slaDays, now);
    const orderCostCents = order.lines.reduce((sum, line) => sum + line.priceCents, 0);
    const procurement = (itemId: string) => procurementByItem.get(itemId) ?? NO_PROCUREMENT;

    for (const line of order.lines) {
      if (line.status !== "PENDING") continue;
      const quantRemaining = remainingByLine.get(line.lineId);
      if (quantRemaining === undefined || !Number.isFinite(quantRemaining) || quantRemaining >= 0) {
        continue;
      }
      const bought = procurement(line.itemId);
      lines.push({
        lineId: line.lineId,
        orderId: order.orderId,
        accountId: order.accountId,
        customerName: order.customerName,
        createdAt: order.createdAt,
        itemId: line.itemId,
        name: line.name,
        imageUrl: line.imageUrl,
        priceCents: line.priceCents,
        printing: line.printing,
        category: line.category,
        sourceUrl: line.sourceUrl,
        onHand: line.onHand,
        quantRemaining,
        orderCostCents,
        slaDays,
        daysUntilOverSla: daysUntil,
        daysOverSla: Math.max(0, -daysUntil),
        isOverSla: daysUntil < 0,
        lastOrderedAt: bought.lastOrderedAt,
        amountProcuredLast4Weeks: bought.amountInWindow,
        procurementDatesLast4Weeks: bought.orderedAtInWindow,
      });
    }
  }

  return { lines, grouped: groupMissingCards(lines) };
}

/** Strict bounds, matching Retool: a bag created at exactly the start or the end is left out. */
function inCreatedRange(createdAt: Date, from: Date | undefined, to: Date | undefined): boolean {
  if (!from && !to) return true;
  if (from && !(createdAt > from)) return false;
  if (to && !(createdAt < to)) return false;
  return true;
}

function groupMissingCards(lines: MissingCardLine[]): GroupedMissingCard[] {
  const grouped = new Map<string, GroupedMissingCard>();
  for (const line of lines) {
    const existing = grouped.get(line.itemId);
    if (!existing) {
      grouped.set(line.itemId, {
        itemId: line.itemId,
        name: line.name,
        imageUrl: line.imageUrl,
        priceCents: line.priceCents,
        printing: line.printing,
        sourceUrl: line.sourceUrl,
        quantRemaining: line.quantRemaining,
        lastOrderedAt: line.lastOrderedAt,
        amountProcuredLast4Weeks: line.amountProcuredLast4Weeks,
        procurementDatesLast4Weeks: line.procurementDatesLast4Weeks,
        earliestOrderAt: line.createdAt,
        orders: [
          {
            orderId: line.orderId,
            accountId: line.accountId,
            customerName: line.customerName,
            createdAt: line.createdAt,
          },
        ],
      });
      continue;
    }
    if (line.quantRemaining < existing.quantRemaining)
      existing.quantRemaining = line.quantRemaining;
    if (line.createdAt < existing.earliestOrderAt) existing.earliestOrderAt = line.createdAt;
    existing.orders.push({
      orderId: line.orderId,
      accountId: line.accountId,
      customerName: line.customerName,
      createdAt: line.createdAt,
    });
  }

  for (const group of grouped.values()) {
    group.orders.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  }
  return [...grouped.values()].sort(
    (a, b) => a.earliestOrderAt.getTime() - b.earliestOrderAt.getTime(),
  );
}

/** One card on the urgent poster: the first short line, plus how many short lines name it. */
export interface UrgentMissingCard {
  itemId: string;
  name: string;
  imageUrl: string | null;
  printing: string | null;
  category: string | null;
  customerName: string | null;
  createdAt: Date | string;
  needed: number;
}

/**
 * Retool's printed list. Keeps lines within {@link URGENT_MISSING_SLA_DAYS} of the deadline,
 * counts every short line of a card, keeps the first, takes the oldest
 * {@link URGENT_MISSING_CARDS_LIMIT}, then sorts that page by category.
 */
export function urgentMissingCards(
  lines: ReadonlyArray<{
    itemId: string;
    name: string;
    imageUrl: string | null;
    printing: string | null;
    category: string | null;
    customerName: string | null;
    createdAt: Date | string;
    daysUntilOverSla: number;
    quantRemaining: number;
  }>,
): UrgentMissingCard[] {
  const short = lines.filter(
    (line) => line.daysUntilOverSla <= URGENT_MISSING_SLA_DAYS && line.quantRemaining < 0,
  );
  const counts = new Map<string, number>();
  for (const line of short) counts.set(line.itemId, (counts.get(line.itemId) ?? 0) + 1);

  const unique = new Map<string, (typeof short)[number]>();
  for (const line of short) {
    if (!unique.has(line.itemId)) unique.set(line.itemId, line);
  }

  const oldest = [...unique.values()]
    .sort((a, b) => timeOf(a.createdAt) - timeOf(b.createdAt))
    .slice(0, URGENT_MISSING_CARDS_LIMIT);
  oldest.sort((a, b) => (a.category ?? "").localeCompare(b.category ?? ""));

  return oldest.map((line) => ({
    itemId: line.itemId,
    name: line.name,
    imageUrl: line.imageUrl,
    printing: line.printing,
    category: line.category,
    customerName: line.customerName,
    createdAt: line.createdAt,
    needed: counts.get(line.itemId) ?? 1,
  }));
}

function timeOf(value: Date | string): number {
  return value instanceof Date ? value.getTime() : new Date(value).getTime();
}

export type PickAvailability = "in_stock" | "short" | "out_of_stock" | "unknown";

export interface PickSheetOrder {
  orderId: string;
  type: OrderType;
  tenantReferenceId: string | null;
  username: string | null;
  externalId: string;
  lineCount: number;
  shipsToCountry: string | null;
  /** The two the printed packing slip needs, so it can be built from a fetch by id. */
  shopperName: string | null;
  createdAt: Date;
}

export interface PickSheetGroup {
  itemId: string;
  name: string;
  imageUrl: string | null;
  category: string | null;
  /** Units to pick across the selected orders. */
  quantity: number;
  /** `catalog_items.quantity` — units on the shelf. Null when the line names no catalog row. */
  onHand: number | null;
  availability: PickAvailability;
  /** Which order each unit belongs to, so a packer can split the pile. */
  picks: Array<{ orderId: string; tenantReferenceId: string | null; lineId: string }>;
}

export interface PickSheet {
  /** The orders this sheet picks, in the order they were asked for. */
  orders: PickSheetOrder[];
  /** One entry per physical card, sorted by name so identical cards sit together. */
  units: PickSheetUnit[];
  groups: PickSheetGroup[];
  /** The `short` and `out_of_stock` subset — what the later Missing Cards port reads. */
  shortages: PickSheetGroup[];
  totals: { orderCount: number; unitCount: number; distinctItemCount: number };
}

/** What the fold needs of an order, so callers may pass a row from either query. */
export interface PickSheetOrderInput {
  id: string;
  type: OrderType;
  tenantReferenceId: string | null;
  endUserId: string;
  shippingAddress: unknown;
  createdAt: Date;
}

export interface PickSheetItemInput {
  id: string;
  name: string;
  image: string | null;
  overrideImage: string | null;
  category: string | null;
  quantity: number;
  attributes: unknown;
}

/**
 * One physical card to pick. The combined card view shows a cell per unit rather than a grouped
 * count, because a picker matches artwork one card at a time: three copies of a card are three
 * cards to find, and collapsing them to "x3" hides which bag each belongs to.
 */
export interface PickSheetUnit {
  lineId: string;
  orderId: string;
  tenantReferenceId: string | null;
  /** The order's position in this sheet, 1-based — what the card view prints as "Order 3". */
  orderIndex: number;
  itemId: string;
  /** Always `PENDING` on the pick sheet; any status in {@link foldOrderCards}. */
  status: OrderLineStatus;
  name: string;
  imageUrl: string | null;
  /** `Holofoil`, `Normal`, `Foil` — the printing, shown in red on the card. */
  printing: string | null;
  setName: string | null;
  gameCategory: string | null;
  valueCents: number;
  onHand: number | null;
  shopperName: string | null;
  orderCreatedAt: Date;
}

/** Reads `attributes.details.<key>`, the jsonb the game, printing and rarity actually live in. */
export function detail(attributes: unknown, key: string): string | null {
  if (attributes === null || typeof attributes !== "object") return null;
  const details = (attributes as Record<string, unknown>).details;
  if (details === null || typeof details !== "object") return null;
  const value = (details as Record<string, unknown>)[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

function collectionName(attributes: unknown): string | null {
  if (attributes === null || typeof attributes !== "object") return null;
  const value = (attributes as Record<string, unknown>).collectionName;
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * The combined card view: every line still to be picked across the selected orders, folded by
 * the item, with the orders each unit belongs to. Pure, so the arithmetic is testable without
 * a database — the loader below does the I/O.
 *
 * A line's `snapshot` supplies the name only when the item is not in the catalog: the snapshot
 * has two shapes (Loot OS native and boxed-migrated) and neither sorts, so catalog facts win
 * wherever they exist.
 */
export function foldPickSheet(args: PickSheetInputs): PickSheet {
  const itemById = indexItems(args.items);
  const userById = new Map(args.users.map((user) => [user.id, user]));
  const picked = args.orders;
  const orderById = new Map(picked.map((order) => [order.id, order]));

  // Only what is still to be picked. A PACKED line is already in the bag *and* already gone from
  // `catalog_items.quantity`, so counting it here would compare a need against stock that was
  // decremented for that very line and report a false shortage — and it would send a picker to
  // find a card someone has already pulled. `allocateStock` skips non-PENDING lines for the same
  // reason, and the queue's in-stock tick and this sheet have to agree about the same bag.
  const toPick = args.lines.filter(
    (line) => line.status === "PENDING" && orderById.has(line.orderId),
  );
  const groups = new Map<string, PickSheetGroup>();

  for (const line of toPick) {
    const item = itemById.get(line.itemId);
    const existing = groups.get(line.itemId);
    const group: PickSheetGroup = existing ?? {
      itemId: line.itemId,
      name: item?.name ?? snapshotName(line.snapshot) ?? line.itemId,
      imageUrl: item ? (item.overrideImage ?? item.image) : snapshotImage(line.snapshot),
      category: item?.category ?? null,
      quantity: 0,
      onHand: item?.quantity ?? null,
      availability: "unknown",
      picks: [],
    };
    group.quantity += 1;
    group.picks.push({
      orderId: line.orderId,
      tenantReferenceId: orderById.get(line.orderId)?.tenantReferenceId ?? null,
      lineId: line.id,
    });
    groups.set(line.itemId, group);
  }

  for (const group of groups.values()) {
    group.availability = resolveAvailability(group.quantity, group.onHand);
  }

  // Category then name: a picker walks the shelves in that order, and it keeps a re-run stable.
  const ordered = [...groups.values()].sort(
    (a, b) => (a.category ?? "").localeCompare(b.category ?? "") || a.name.localeCompare(b.name),
  );

  const units = toCardUnits(toPick, { picked, itemById });

  const linesByOrder = new Map<string, number>();
  for (const line of args.lines) {
    if (line.status === "CANCELLED") continue;
    linesByOrder.set(line.orderId, (linesByOrder.get(line.orderId) ?? 0) + 1);
  }
  const toSheetOrder = (order: PickSheetOrderInput): PickSheetOrder => ({
    orderId: order.id,
    type: order.type,
    tenantReferenceId: order.tenantReferenceId,
    username: userById.get(order.endUserId)?.username ?? null,
    externalId: userById.get(order.endUserId)?.externalId ?? "",
    lineCount: linesByOrder.get(order.id) ?? 0,
    shipsToCountry: orderAddressCountry(order.shippingAddress),
    shopperName: readAddressName(order.shippingAddress),
    createdAt: order.createdAt,
  });

  return {
    orders: picked.map(toSheetOrder),
    units,
    groups: ordered,
    shortages: ordered.filter(
      (group) => group.availability === "short" || group.availability === "out_of_stock",
    ),
    totals: {
      orderCount: picked.length,
      unitCount: toPick.length,
      distinctItemCount: ordered.length,
    },
  };
}

/**
 * Every line of the given orders as one card each, whatever its status: TCG Order List's View
 * Cards (one order) and Combined Card View (the selection), which showed a bag's packed and
 * cancelled cards too, where the pick sheet shows only what is left to pick. Pure, like
 * {@link foldPickSheet}, and read by the same loader.
 */
export function foldOrderCards(args: OrderCardInputs): PickSheetUnit[] {
  // The loader reads only these orders' lines; the fold still keeps to them, as foldPickSheet
  // does, so it stays correct for any inputs it is handed.
  const orderIds = new Set(args.orders.map((order) => order.id));
  return toCardUnits(
    args.lines.filter((line) => orderIds.has(line.orderId)),
    { picked: args.orders, itemById: indexItems(args.items) },
  );
}

/**
 * One card per line, numbered by its order's position in `picked` (the green "Order 3") and
 * sorted by name, which is what puts identical cards next to each other on the picking bench.
 */
function toCardUnits(
  lines: OrderLine[],
  context: { picked: PickSheetOrderInput[]; itemById: Map<string, PickSheetItemInput> },
): PickSheetUnit[] {
  const orderById = new Map(context.picked.map((order) => [order.id, order]));
  const orderIndex = new Map(context.picked.map((order, index) => [order.id, index + 1]));
  const units = lines.map((line): PickSheetUnit => {
    const item = context.itemById.get(line.itemId);
    const order = orderById.get(line.orderId);
    return {
      lineId: line.id,
      orderId: line.orderId,
      tenantReferenceId: order?.tenantReferenceId ?? null,
      orderIndex: orderIndex.get(line.orderId) ?? 0,
      itemId: line.itemId,
      status: line.status,
      name: item?.name ?? snapshotName(line.snapshot) ?? line.itemId,
      // The snapshot covers far more lines than the catalog does (210/213 against 60/167), and
      // it is the image the shopper was shown, so it wins here.
      imageUrl: snapshotImage(line.snapshot) ?? (item ? (item.overrideImage ?? item.image) : null),
      printing: detail(item?.attributes, "printing") ?? detail(line.snapshot, "printing"),
      setName: collectionName(item?.attributes) ?? snapshotText(line.snapshot, "collectionName"),
      // `detail` walks into `.details` itself, so the snapshot goes in whole. Passing
      // `snapshot.details` read `details.details` and came back null for every migrated line.
      gameCategory: detail(item?.attributes, "category") ?? detail(line.snapshot, "category"),
      valueCents: line.unitValue,
      onHand: item?.quantity ?? null,
      shopperName: readAddressName(order?.shippingAddress),
      orderCreatedAt: order?.createdAt ?? new Date(0),
    };
  });
  units.sort((a, b) => a.name.localeCompare(b.name) || a.orderIndex - b.orderIndex);
  return units;
}

/**
 * `catalog_items.quantity` counts units physically on the shelf, and a line in this queue has
 * already taken its reservation (`reserveStock` raises `withdrawing_count`, pack lowers both).
 * So the question a picker asks is whether the shelf holds what this sheet needs — comparing
 * against `quantity - withdrawing_count` would subtract this sheet's own reservations twice.
 */
function resolveAvailability(needed: number, onHand: number | null): PickAvailability {
  if (onHand === null) return "unknown";
  if (onHand <= 0) return "out_of_stock";
  return onHand >= needed ? "in_stock" : "short";
}

function snapshotName(snapshot: Record<string, unknown>): string | null {
  const name = snapshot.name;
  return typeof name === "string" && name.length > 0 ? name : null;
}

function snapshotText(snapshot: Record<string, unknown>, key: string): string | null {
  const value = snapshot[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

function snapshotImage(snapshot: Record<string, unknown>): string | null {
  const image = snapshot.imageUrl ?? snapshot.image;
  return typeof image === "string" && image.length > 0 ? image : null;
}

/** What {@link foldOrderCards} folds, loaded by {@link loadOrderCardInputs}. */
export interface OrderCardInputs {
  orders: PickSheetOrderInput[];
  lines: OrderLine[];
  items: PickSheetItemInput[];
}

/** What {@link foldPickSheet} folds, loaded by {@link loadPickSheetInputs}: the cards' inputs and the shoppers. */
export interface PickSheetInputs extends OrderCardInputs {
  users: Array<{ id: string; username: string | null; externalId: string }>;
}

/** The catalog rows by id, the one join both folds make. */
function indexItems(items: PickSheetItemInput[]): Map<string, PickSheetItemInput> {
  return new Map(items.map((item) => [item.id, item]));
}
